import { expressAddClass, expressEventListener, expressQuerySelector, expressRemoveClass } from "../../common/html";
import { compositeDisposable, IDisposable } from "../../utils/disposable";

export interface ITextFieldEdit {
	readonly getText: () => string;
	readonly dispose: () => void;
}

export interface ICreateTextFieldEditDependencies {
	onEditFinished: (text: string) => Promise<void>;
}
export interface ICreateTextFieldEditOptions {
	autoUpdateOnChange?: boolean;
	updateOnEnter?: boolean;
}

export function createTextFieldEdit(
	containerEl: HTMLElement,
	deps: ICreateTextFieldEditDependencies,
	opts?: ICreateTextFieldEditOptions,
) {
	const inputEl = expressQuerySelector<HTMLInputElement>(containerEl, "input", true);
	const spanEl = expressQuerySelector<HTMLInputElement>(containerEl, ".a-form-input__copy", true);
	const iconEl =
		expressQuerySelector<HTMLElement>(containerEl, ".a-icon", false) ||
		expressQuerySelector<HTMLElement>(containerEl, ".v2-a-icon", true);
	const { autoUpdateOnChange = false, updateOnEnter = false } = opts;
	const { onEditFinished } = deps;
	let saving = false; // Just a way to know if the component is saving (api call)
	let origContent = inputEl.value.trim();
	let disposable: IDisposable;
	const defaultAlbumName = inputEl.dataset.defaultname || "";

	const onIconClick = () => {
		expressAddClass(iconEl, "u-hide");
		inputEl.focus();
	};

	const resizeInputEl = () => {
		const inputSize = spanEl.offsetWidth + 10;
		inputEl.style.width = inputSize + "px";
	};

	const onInput = () => {
		if (inputEl.value.trim()) {
			spanEl.textContent = inputEl.value.trim();
			resizeInputEl();
			if (autoUpdateOnChange && !saving) save();
		}
	};

	const onEnter = (ev: KeyboardEvent) => {
		if (ev.keyCode === 13 || ev.key === "Enter") {
			ev.preventDefault();
			if (updateOnEnter && !saving) {
				save();
			}
		}
	};

	const save = () => {
		if (inputEl.value !== origContent) {
			origContent = inputEl.value.trim();
			saving = true;
			onEditFinished(origContent).then((_) => {
				saving = false;
				inputEl.blur();
				expressRemoveClass(iconEl, "u-hide");
			});
		} else {
			inputEl.blur();
			expressRemoveClass(iconEl, "u-hide");
		}
	};

	const onFocusOut = () => {
		if (!inputEl.value.trim()) {
			inputEl.value = defaultAlbumName;
			onInput();
		}
		if (!saving) save();
	};
	const onFocus = () => {
		if (inputEl.value === defaultAlbumName) {
			inputEl.value = "";
		}
		expressAddClass(iconEl, "u-hide");
	};

	const addHandlers = () => {
		disposable = compositeDisposable([
			expressEventListener(iconEl, "click", onIconClick),
			expressEventListener(inputEl, "input", onInput),
			expressEventListener(inputEl, "keydown", onEnter),
			expressEventListener(inputEl, "focusout", onFocusOut),
			expressEventListener(inputEl, "focus", onFocus),
		]);
	};

	const init = () => {
		addHandlers();
		resizeInputEl();

		expressRemoveClass(inputEl, "skeleton-loading");
		expressRemoveClass(iconEl, "skeleton-loading");
	};

	init();

	return {
		getText: () => inputEl.textContent,
		dispose: () => disposable.dispose(),
	} as ITextFieldEdit;
}
